import React, { useState, useEffect, useContext } from "react";
import warningIcon from "../../assets/images/product-knowledge-request-form-art.png";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react";
import {
  Root,
  Container,
  PageTitle,
  AttendeesSection,
  AttendeeRow,
  AttendeeFields,
  InputField,
  PointsLabel,
  ErrorMessage,
  InsufficientPointsMessage,
  NotEnoughPointsLabel,
  BookingPrompt,
  AddMoreSection,
  DeleteButton,
  TotalSection,
  TotalRow,
  ContinueButton,
  BackLink,
} from "./AttendeesView.style";
import { Layout } from "../../components/Layout/Layout";
import { Header } from "../../components/Header/Header";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { Footer } from "../../components/Footer/Footer";
import { Loading } from "../../components/Loading/Loading";
import { Typography } from "../../ui/Typography/Typography";
import {
  toast,
  ToastNotification,
} from "../../ui/ToastNotification/ToastNotification";
import {
  GET_EDUCATION_REWARD_FOR_ATTENDEES,
  GET_POINTS_BALANCE,
  ADD_TO_CART_EDUCATION,
  UPDATE_CART_EDUCATION,
  UPDATE_CART_ATTENDEES,
} from "./attendeesViewConfig";
import { UserContext } from "../../context/UserContext";
import { GET_LOYALTY_TIER } from "../../graphql/queries/me";
import {
  normalizeLoyaltyTier,
  formatEligibleTiersMessage,
} from "../../utils/clubhTier";

export const AttendeesView = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { user } = useContext(UserContext);

  // qty is managed locally — starts at 1, changes when user adds/removes attendees
  const [qty, setQty] = useState(1);
  const [attendees, setAttendees] = useState([{ name: "", email: "" }]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { loading: productLoading, error: productError, data: productData } = useQuery(
    GET_EDUCATION_REWARD_FOR_ATTENDEES,
    { variables: { id: productId, locale: intl.locale } }
  );

  const { loading: balanceLoading, data: balanceData } = useQuery(GET_POINTS_BALANCE, {
    variables: { locale: intl.locale },
  });

  const { data: loyaltyTierData, loading: loyaltyTierLoading } = useQuery(GET_LOYALTY_TIER, {
    skip: user?._id == null,
    fetchPolicy: 'cache-first',
  });

  const [addToCart] = useMutation(ADD_TO_CART_EDUCATION, {
    refetchQueries: ["GetCartDetails", "GetHeaderCartData"],
  });

  const [updateCart] = useMutation(UPDATE_CART_EDUCATION, {
    refetchQueries: ["GetCartDetails", "GetHeaderCartData"],
  });

  const [updateCartAttendees] = useMutation(UPDATE_CART_ATTENDEES, {
    refetchQueries: ["GetCartDetails", "GetCartWithEducationReward"],
  });

  const educationReward = productData && productData.content && productData.content.findById;
  const pricePerAttendee = (educationReward && educationReward.ecommerce && educationReward.ecommerce.price && educationReward.ecommerce.price.amount) || 0;
  const pointsBalance = (balanceData && balanceData.me && balanceData.me.pointsBalance) || 0;

  // Check if this product is already in the cart
  const cartItems = (balanceData && balanceData.me && balanceData.me.cart && balanceData.me.cart.items) || [];
  const existingCartItem = cartItems.find(function(item) { return item.product && item.product._id === productId; });
  const category = educationReward && educationReward.data && educationReward.data.category;
  const bookingLink = educationReward && educationReward.data && educationReward.data.bookingLink;

  // Tier restriction check
  const qualifyingTiers = Array.isArray(
    educationReward && educationReward.data && educationReward.data.qualifyingTiers,
  )
    ? educationReward.data.qualifyingTiers
    : [];
  const isTierRestricted = qualifyingTiers.length > 0;
  const computedTier =
    user?._id != null
      ? normalizeLoyaltyTier(loyaltyTierData?.me?.loyaltyTier)
      : null;
  const isTierEligible =
    !isTierRestricted ||
    (user?._id == null) ||
    (!loyaltyTierLoading &&
      computedTier != null &&
      qualifyingTiers.some((t) => t.toLowerCase() === computedTier.toLowerCase()));
  const tierDisabled = user?._id != null && isTierRestricted && (loyaltyTierLoading || !isTierEligible);
  const tierDisabledMessage = isTierRestricted ? formatEligibleTiersMessage(qualifyingTiers) : null;

  // Keep attendees array in sync with qty
  useEffect(() => {
    setAttendees((prev) => {
      if (qty > prev.length) {
        const extra = Array.from({ length: qty - prev.length }, () => ({ name: "", email: "" }));
        return prev.concat(extra);
      }
      return prev.slice(0, qty);
    });
  }, [qty]);

  const handleAddMoreAttendee = () => {
    const newTotal = pricePerAttendee * (qty + 1);
    if (pointsBalance < newTotal) {
      return;
    }
    setQty(qty + 1);
  };

  const handleRemoveAttendee = (indexToRemove) => {
    if (qty <= 1 || attendees.length <= 1) {
      toast.warn(
        <ToastNotification>
          <FormattedMessage defaultMessage="At least one attendee is required." />
        </ToastNotification>
      );
      return;
    }
    setQty(qty - 1);
    setAttendees((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleInputChange = (index, field, value) => {
    const newAttendees = [...attendees];
    newAttendees[index][field] = value;
    setAttendees(newAttendees);

    const errorKey = `${index}-${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    attendees.forEach((attendee, index) => {
      if (!attendee.name || attendee.name.trim() === "") {
        newErrors[`${index}-name`] = "Name is required";
        isValid = false;
      }
      if (!attendee.email || attendee.email.trim() === "") {
        newErrors[`${index}-email`] = "Email is required";
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendee.email)) {
        newErrors[`${index}-email`] = "Invalid email format";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (tierDisabled) {
      toast.dismiss();
      toast.warn(
        <ToastNotification>
          {tierDisabledMessage || (
            <FormattedMessage defaultMessage="Your membership tier is not eligible to redeem this reward." />
          )}
        </ToastNotification>,
      );
      return;
    }

    if (!validateForm()) {
      toast.error(
        <ToastNotification>
          <FormattedMessage defaultMessage="Please fill in all required fields correctly." />
        </ToastNotification>
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Add to cart or update qty if item already exists
      if (existingCartItem) {
        await updateCart({
          variables: {
            productId,
            qty,
            locale: intl.locale,
          },
        });
      } else {
        await addToCart({
          variables: {
            productId,
            qty,
            locale: intl.locale,
          },
        });
      }

      // Step 2: Save attendee details to the cart item
      await updateCartAttendees({
        variables: {
          productId,
          attendees,
          locale: intl.locale,
        },
      });

      // Navigate to cart
      navigate(`/${intl.locale}/cart`);
    } catch (err) {
      Sentry.captureException(err);

      const graphqlMessage =
        err.graphQLErrors && err.graphQLErrors[0] && err.graphQLErrors[0].message;
      const errorMessage = graphqlMessage || err.message || "An error occurred";

      toast.error(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="Error: {message}"
            values={{ message: errorMessage }}
          />
        </ToastNotification>
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (productLoading || balanceLoading || (isTierRestricted && loyaltyTierLoading)) {
    return <Loading global />;
  }

  if (productError || !educationReward) {
    return (
      <Layout>
        <Header />
        <Container>
          <Typography type="h1">
            <FormattedMessage defaultMessage="Error" />
          </Typography>
          <Typography>
            <FormattedMessage defaultMessage="Unable to load class details. Please try again." />
          </Typography>
        </Container>
        <Footer />
      </Layout>
    );
  }

  const totalPoints = pricePerAttendee * qty;
  const hasInsufficientPoints = pointsBalance < totalPoints;

  const categoryLabel =
    category === "classroom-education"
      ? intl.formatMessage({ defaultMessage: "Academy Education" })
      : intl.formatMessage({ defaultMessage: "Events & Experiences" });

  return (
    <Layout>
      <Header />
      <Breadcrumbs
        breadcrumbs={[
          { to: "/", label: intl.formatMessage({ defaultMessage: "Home" }) },
          {
            to: "/education",
            label: intl.formatMessage({ defaultMessage: "Education" }),
          },
          {
            to:
              category === "classroom-education"
                ? "/education/academy-education"
                : "/education/events-experiences",
            label: categoryLabel,
          },
          {
            to: null,
            label: educationReward.n,
          },
        ]}
      />

      <Root>
        <Container>
          <PageTitle>
            <FormattedMessage defaultMessage="ENTER ATTENDEES DETAILS" />
          </PageTitle>

          <AttendeesSection>
            {attendees.map((attendee, index) => (
              <AttendeeRow key={index}>
                <AttendeeFields>
                  {index > 0 && (
                    <DeleteButton
                      type="button"
                      onClick={() => handleRemoveAttendee(index)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </DeleteButton>
                  )}
                  <div>
                    <InputField
                      type="text"
                      placeholder={intl.formatMessage({
                        defaultMessage: "First Name",
                      })}
                      value={attendee.name.split(" ")[0] || ""}
                      onChange={(e) => {
                        const lastName = attendee.name.split(" ").slice(1).join(" ");
                        handleInputChange(
                          index,
                          "name",
                          lastName ? `${e.target.value} ${lastName}` : e.target.value
                        );
                      }}
                      error={errors[`${index}-name`]}
                    />
                    {errors[`${index}-name`] && (
                      <ErrorMessage>{errors[`${index}-name`]}</ErrorMessage>
                    )}
                  </div>
                  <div>
                    <InputField
                      type="text"
                      placeholder={intl.formatMessage({
                        defaultMessage: "Last Name",
                      })}
                      value={attendee.name.split(" ").slice(1).join(" ") || ""}
                      onChange={(e) => {
                        const firstName = attendee.name.split(" ")[0] || "";
                        handleInputChange(
                          index,
                          "name",
                          `${firstName} ${e.target.value}`.trim()
                        );
                      }}
                    />
                  </div>
                  <div>
                    <InputField
                      type="email"
                      placeholder={intl.formatMessage({
                        defaultMessage: "Email",
                      })}
                      value={attendee.email}
                      onChange={(e) =>
                        handleInputChange(index, "email", e.target.value)
                      }
                      error={errors[`${index}-email`]}
                    />
                    {errors[`${index}-email`] && (
                      <ErrorMessage>{errors[`${index}-email`]}</ErrorMessage>
                    )}
                  </div>
                </AttendeeFields>
                <PointsLabel>
                  {pricePerAttendee.toLocaleString("en-US")}{" "}
                  <FormattedMessage defaultMessage="PTS" />
                </PointsLabel>
              </AttendeeRow>
            ))}
          </AttendeesSection>

          {pointsBalance < pricePerAttendee * (qty + 1) ? (
            <InsufficientPointsMessage>
              <NotEnoughPointsLabel>
                <FormattedMessage defaultMessage="Not enough points to add more attendees" />{" "}
                <img src={warningIcon} alt="warning" style={{ width: 20, height: 20 }} />
              </NotEnoughPointsLabel>
              {category === "events-experiences" ? (
                <BookingPrompt>
                  <a href="mailto:education.ca@henkel.com">
                    <FormattedMessage defaultMessage="Contact" />
                  </a>{" "}
                  <FormattedMessage defaultMessage="us directly and we'll get in touch with you." />
                </BookingPrompt>
              ) : bookingLink ? (
                <BookingPrompt>
                  <a href={bookingLink} target="_blank" rel="noopener noreferrer">
                    <FormattedMessage defaultMessage="Book" />
                  </a>{" "}
                  <FormattedMessage defaultMessage="directly with our distributor partner" />
                </BookingPrompt>
              ) : (
                <BookingPrompt>
                  <FormattedMessage defaultMessage="Book directly with our distributor partner" />
                </BookingPrompt>
              )}
            </InsufficientPointsMessage>
          ) : (
            <AddMoreSection
              type="button"
              onClick={handleAddMoreAttendee}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <FormattedMessage defaultMessage="Add More Attendees" />
            </AddMoreSection>
          )}

          <TotalSection>
            <TotalRow muted={hasInsufficientPoints}>
              <span>
                <FormattedMessage defaultMessage="TOTAL:" />
              </span>
              <span>
                {totalPoints.toLocaleString("en-US")}{" "}
                <FormattedMessage defaultMessage="PTS" />
              </span>
            </TotalRow>
          </TotalSection>

          {tierDisabled && tierDisabledMessage && (
            <InsufficientPointsMessage>
              <NotEnoughPointsLabel>
                {tierDisabledMessage}
              </NotEnoughPointsLabel>
            </InsufficientPointsMessage>
          )}

          <ContinueButton
            onClick={handleSubmit}
            disabled={isSubmitting || hasInsufficientPoints || tierDisabled}
          >
            <FormattedMessage defaultMessage="CONTINUE" />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              fill="currentColor"
            >
              <path d="m3.695 1.708 5.11 5.1v.389l-5.11 5.1 1.06 1.055 5.549-5.539V6.192L4.755.649l-1.06 1.059Z" />
            </svg>
          </ContinueButton>
        </Container>
      </Root>

      <Footer />
    </Layout>
  );
};
